/**
 * InterventionsPage - Tableau de bord des interventions pour administrateurs
 * Affiche la liste détaillée des interventions avec filtres
 * Par défaut, affiche uniquement les interventions non archivées
 *
 * v3 — Supabase-first : appelle GET /api/interventions (plus de /api/sync/*)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { passeLaCarte } from '../utils/filtreCartes';
import {
  RefreshIcon,
  CloseIcon,
  SearchIcon,
  EyeIcon,
  UserIcon,
  WrenchIcon,
  AlertTriangleIcon,
  TruckIcon,
  ArchiveIcon,
  HistoryIcon,
  MapPinIcon,
  ShieldIcon
} from '../icons';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';
import { useViewport } from '../hooks/useViewport';
import { useHasPermission } from '../SupabaseWrapper';

// ================================================================
// Types v3 — matchent le format _toPayloadFormat() du backend
// ================================================================

interface InterventionStatut {
  code: string;
  libelle: string;
  app_status: string;
}

interface InterventionClient {
  code: string | null;
  nom: string | null;
  nom2: string | null;
  adresse1: string | null;
  adresse2: string | null;
  complement_ad1: string | null;
  complement_ad2: string | null;
  code_postal: string | null;
  ville: string | null;
  telephone: string | null;
  telephone_bureau: string | null;
  mobile: string | null;
  email: string | null;
  email_bureau: string | null;
}

interface InterventionAppareil {
  fournisseur_code: string | null;
  fournisseur_nom: string | null;
  code_article: string | null;
  designation: string | null;
  marque_code: string | null;
  marque: string | null;
  type_code: string | null;
  type: string | null;
  famille_code: string | null;
  famille: string | null;
  numero_serie: string | null;
  code_ean: string | null;
  compteur: string | null;
  date_achat: string | null;
  aspect_code: string | null;
  aspect: string | null;
  garantie_code: string | null;
  garantie: string | null;
  garantie_ref: string | null;
  accessoires_nombre: number | null;
  accessoires_libelle: string | null;
}

interface InterventionTechnicien {
  code: string | null;
  nom: string | null;
  sous_traitant_code: string | null;
  chez_sous_traitant: string | null;
}

interface InterventionPanne {
  nature: string | null;
  message_facture: string | null;
}

interface InterventionLogistique {
  type_prestation_code: string | null;
  type_prestation: string | null;
  type_reparation_code: string | null;
  origine_code: string | null;
  origine: string | null;
  atelier_code: string | null;
  atelier: string | null;
  duree_reparation: string | null;
  emplacement: string | null;
}

interface InterventionMontants {
  maxi: number | null;
  facture: number | null;
  ht_stock: number | null;
  revient: number | null;
  acompte_1: number | null;
  acompte_2: number | null;
  acompte_3: number | null;
  acompte_4: number | null;
}

interface InterventionDates {
  emission: string | null;
  heure_emission: string | null;
  prevue: string | null;
  facture: string | null;
  relance: string | null;
}

interface InterventionTravaux {
  compte_rendu: string | null;
  travaux_effectues: string | null;
  observations: string | null;
}

interface InterventionTotaux {
  pieces: number | null;
  main_oeuvre: number | null;
  ttc: number | null;
  passages_count: number | null;
  pieces_count: number | null;
  payments_total: number | null;
}

interface InterventionArchivage {
  is_archived: boolean;
  archived_at: string | null;
  archived_reason: string | null;
}

interface InterventionMetadata {
  created_at: string | null;
  updated_at: string | null;
  sync_status: string | null;
}

interface InterventionQuotas {
  quantite_realisee: number | null;
  quantite_planifiee: number | null;
  montant_debit: number | null;
  secteur: string | null;
  code_postal: string | null;
  date_maj: string | null;
  utilisateur: string | null;
  date_debit: string | null;
}

interface PassagePhoto {
  id: string;
  url: string;
  storage_path?: string;
  photo_type: string;
  description?: string;
  display_order: number;
  created_at: string;
}

interface PassagePiece {
  id: string;
  article_code: string;
  article_description: string;
  article_reference?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  observations?: string;
}

interface PassagePayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_type?: string;
  reference?: string;
  payment_date?: string;
  observations?: string;
}

interface PassageLine {
  id: string;
  line_number: number;
  line_type: string;
  status_code: string | null;
  observations: string | null;
  created_at: string;
}

interface Passage {
  passage_number: number;
  started_at: string | null;
  closed_at: string | null;
  duration_minutes: number | null;
  statut_initial: string | null;
  statut_final: string | null;
  lines: PassageLine[];
  pieces: PassagePiece[];
  photos: PassagePhoto[];
  payments: PassagePayment[];
}

interface BonIntervention {
  url: string;
  filename: string;
  passage_number: number;
  created_at: string;
}

interface PassagesData {
  passages: Passage[];
  totaux: {
    passages_count: number;
    pieces_count: number;
    photos_count: number;
    payments_count: number;
    payments_total: number;
    payments_card: number;
    payments_cash: number;
    payments_check: number;
    duree_totale_minutes: number;
  };
  bons_intervention?: BonIntervention[];
}

interface InterventionDetail {
  id: string;
  external_id: string | null;
  external_source: string | null;
  numero_ordre: string | null;
  statut: InterventionStatut;
  date_intervention: string | null;
  client: InterventionClient;
  appareil: InterventionAppareil;
  panne: InterventionPanne;
  technicien: InterventionTechnicien;
  intervention: InterventionLogistique;
  quotas?: InterventionQuotas;
  montants: InterventionMontants;
  dates: InterventionDates;
  travaux: InterventionTravaux;
  totaux: InterventionTotaux;
  archivage: InterventionArchivage;
  metadata: InterventionMetadata;
}

/** Réponse de GET /api/interventions */
interface ApiResponse {
  success: boolean;
  count: number;
  total: number;
  interventions: InterventionDetail[];
  statistiques: {
    total: number;
    par_code_statut: Record<string, number>;
  };
}

// Types pour les passages (modal historique — API pas encore disponible en v3)
interface PassagePhoto {
  id: string;
  url: string;
  storage_path?: string;
  type: string;
  description?: string;
  ordre: number;
  created_at: string;
}

// Configuration API
const API_BASE = window.location.origin;

// Traduction des statuts techniques en français
const repairStatusLabels: Record<string, string> = {
  'pending': 'À traiter',
  'scheduled': 'Planifié',
  'in_progress': 'En cours',
  'on_hold': 'En attente',
  'completed': 'Terminé',
  'cancelled': 'Annulé'
};

const getAppStatusLabel = (status: string | undefined | null): string => {
  if (!status) return '-';
  return repairStatusLabels[status] || status;
};

// Props du composant
interface InterventionsPageProps {
  refreshTrigger?: number;
}

// ================================================================
// Carte mobile pour une intervention (rendue par ResponsiveCardList sur < 768px)
// ================================================================

interface InterventionMobileCardProps {
  intervention: InterventionDetail;
  statusColor: string;
  statusIsMapped: boolean;
  formattedDate: string;
  onOpenDetail: () => void;
  onOpenPassages: () => void;
}

function InterventionMobileCard({
  intervention,
  statusColor,
  statusIsMapped,
  formattedDate,
  onOpenDetail,
  onOpenPassages,
}: InterventionMobileCardProps) {
  const clientName = intervention.client?.nom || 'Client inconnu';
  const orderNumber = intervention.numero_ordre || intervention.external_id || '';
  const statusCode = intervention.statut?.code || '';
  const statusLibelle = intervention.statut?.libelle || '';
  const techName = intervention.technicien?.nom;
  const techCode = intervention.technicien?.code;
  const city = intervention.client?.ville;
  const isArchived = intervention.archivage?.is_archived || false;

  return (
    <div
      onClick={onOpenDetail}
      style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 14,
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {/* Header : client + N° OR | badge statut */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#1e293b',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {clientName}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            N° {orderNumber || '-'}
            {isArchived && (
              <span style={{
                marginLeft: 8,
                padding: '1px 6px',
                borderRadius: 4,
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                fontSize: 10,
                fontWeight: 600,
              }}>
                Archivée
              </span>
            )}
          </div>
        </div>
        {(statusCode || statusLibelle) && (
          <span
            title={`${statusCode} - ${statusLibelle}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
              ...(statusIsMapped ? {
                backgroundColor: `${statusColor}18`,
                color: statusColor,
              } : {
                backgroundColor: 'transparent',
                border: '1px solid #374151',
                color: '#374151',
              }),
            }}
          >
            <span style={{ fontWeight: 700 }}>{statusCode || '-'}</span>
            <span style={{ fontWeight: 400 }}>{statusLibelle}</span>
          </span>
        )}
      </div>

      {/* Ligne contextuelle : technicien, date, ville */}
      <div style={{
        fontSize: 12,
        color: '#475569',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        rowGap: 4,
      }}>
        {techName && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <UserIcon size={12} color="#64748b" />
            {techName}
            {techCode && (
              <span style={{
                padding: '1px 5px',
                backgroundColor: '#e0e7ff',
                color: '#4338ca',
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 600,
                marginLeft: 2,
              }}>
                {techCode}
              </span>
            )}
          </span>
        )}
        {formattedDate && formattedDate !== '-' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span aria-hidden="true">📅</span>
            {formattedDate}
          </span>
        )}
        {city && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPinIcon size={12} color="#64748b" />
            {city}
          </span>
        )}
      </div>

      {/* Actions : historique (la modale détail s'ouvre au tap sur la carte) */}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onOpenPassages(); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            border: '1px solid #c4b5fd',
            backgroundColor: '#f5f3ff',
            color: '#6d28d9',
            cursor: 'pointer',
          }}
        >
          <HistoryIcon size={12} color="#6d28d9" />
          Historique
        </button>
      </div>
    </div>
  );
}

// ================================================================
// Dropdown de filtre multi-select générique (liste de libellés)
// Réutilisé pour Secteur / Origine / Atelier — même rendu que les
// dropdowns Technicien/Statut existants (styles inline).
// ================================================================

interface ListFilterDropdownProps {
  label: string;
  accent: string;        // couleur bordure/texte quand actif
  activeBg: string;      // fond quand actif
  options: string[];
  selected: string[];
  onToggleValue: (value: string) => void;
  onClear: () => void;
  isOpen: boolean;
  onToggleOpen: () => void;
  searchable?: boolean;
  minWidth?: number;
}

function ListFilterDropdown({
  label, accent, activeBg, options, selected,
  onToggleValue, onClear, isOpen, onToggleOpen,
  searchable = false, minWidth = 140,
}: ListFilterDropdownProps) {
  const [search, setSearch] = useState('');
  const isActive = selected.length > 0;
  const visibleOptions = searchable && search.trim()
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="multi-select-dropdown" style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleOpen(); }}
        style={{
          padding: '9px 14px',
          border: `1px solid ${isActive ? accent : '#e2e8f0'}`,
          borderRadius: '8px',
          fontSize: '13px',
          backgroundColor: isActive ? activeBg : 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: `${minWidth}px`
        }}
      >
        <span style={{ color: isActive ? accent : '#64748b' }}>
          {label} {isActive && `(${selected.length})`}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 100,
          minWidth: '220px',
          maxHeight: '320px',
          overflow: 'hidden'
        }}>
          {searchable && (
            <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>
          )}
          <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 0' }}>
            {visibleOptions.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: '12px', color: '#94a3b8' }}>
                Aucune valeur
              </div>
            )}
            {visibleOptions.map(opt => (
              <label
                key={opt}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  backgroundColor: selected.includes(opt) ? activeBg : 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selected.includes(opt) ? activeBg : 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => onToggleValue(opt)}
                  style={{ accentColor: accent }}
                />
                <span style={{ color: '#374151' }}>{opt}</span>
              </label>
            ))}
          </div>
          {isActive && (
            <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={onClear}
                style={{
                  width: '100%',
                  padding: '6px',
                  backgroundColor: '#f1f5f9',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                Effacer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Barre de filtres responsive
// - Desktop : barre inline (les contrôles passés en children).
// - Mobile / tablette : bouton « Filtres » (+ compteur) ouvrant une modale
//   (bottom-sheet sur mobile, dialogue centré sur tablette) contenant
//   exactement les mêmes contrôles, empilés en pleine largeur.
// ================================================================

const FunnelIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

interface FilterBarResponsiveProps {
  isDesktop: boolean;
  isPhone: boolean;
  activeFiltersCount: number;
  /** true si au moins un état est réinitialisable : filtre actif, recherche non vide,
   *  ou « Archivé » différent du défaut. Volontairement plus large que
   *  activeFiltersCount, qui ne compte ni la recherche ni un « Archivé » vidé.
   *  Consommée uniquement par la branche desktop. */
  canReset: boolean;
  resultsCount: number;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onReset: () => void;
  children: React.ReactNode;
}

function FilterBarResponsive({
  isDesktop, isPhone, activeFiltersCount, canReset, resultsCount,
  open, onOpen, onClose, onReset, children,
}: FilterBarResponsiveProps) {
  const counter = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: isDesktop ? 'auto' : undefined }}>
      {activeFiltersCount > 0 && (
        <span style={{
          padding: '4px 10px',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 500
        }}>
          {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}
        </span>
      )}
      {/* Réinitialisation globale — desktop uniquement : en mobile/tablette, le bouton
          « Réinitialiser » vit dans le pied de la modale de filtres (voir plus bas).
          Le bloc counter étant rendu dans les deux modes, la garde isDesktop est
          indispensable pour ne pas afficher deux boutons en mobile. */}
      {isDesktop && canReset && (
        <button
          onClick={onReset}
          title="Réinitialiser tous les filtres"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
          style={{
            padding: '7px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            backgroundColor: 'white',
            color: '#475569',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Réinitialiser
        </button>
      )}
      <span style={{ fontSize: '13px', color: '#64748b' }}>
        {resultsCount} OR
      </span>
    </div>
  );

  // Desktop : barre inline classique
  if (isDesktop) {
    return (
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {children}
        {counter}
      </div>
    );
  }

  // Mobile / tablette : bouton déclencheur + modale
  const bottomSheet = isPhone;
  const hasActive = activeFiltersCount > 0;

  return (
    <>
      {/* Styles scopés à la modale : ne peuvent pas s'exprimer en styles inline
          (keyframes, mise en pleine largeur des contrôles enfants, reduced-motion). */}
      <style>{`
        @keyframes orFilterOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes orFilterPanelIn {
          from { opacity: 0; transform: translateY(24px) scale(0.985); }
          to   { opacity: 1; transform: none; }
        }
        .or-filter-body > .multi-select-dropdown { width: 100%; }
        .or-filter-body > .multi-select-dropdown > button:first-child { width: 100%; justify-content: space-between; }
        .or-filter-body > .or-filter-search { width: 100%; max-width: none; }
        .or-filter-body > .or-filter-search input { width: 100%; }
        @media (prefers-reduced-motion: reduce) {
          .or-filter-overlay, .or-filter-panel { animation: none !important; }
        }
      `}</style>

      {/* Barre compacte : bouton déclencheur + compteur */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={onOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: `1px solid ${hasActive ? '#3b82f6' : '#cbd5e1'}`,
            borderRadius: '10px',
            backgroundColor: hasActive ? '#eff6ff' : 'white',
            color: hasActive ? '#2563eb' : '#475569',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(15,23,42,0.05)'
          }}
        >
          <FunnelIcon size={16} />
          Filtres
          {hasActive && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              backgroundColor: '#2563eb',
              color: 'white',
              fontSize: 11,
              fontWeight: 700
            }}>
              {activeFiltersCount}
            </span>
          )}
        </button>
        {counter}
      </div>

      {open && (
        <div
          className="or-filter-overlay"
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: bottomSheet ? 'flex-end' : 'center',
            justifyContent: 'center',
            padding: bottomSheet ? 0 : '24px',
            animation: 'orFilterOverlayIn 0.2s ease'
          }}
        >
          <div
            className="or-filter-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              width: '100%',
              maxWidth: bottomSheet ? '100%' : '460px',
              maxHeight: bottomSheet ? '90vh' : '85vh',
              borderRadius: bottomSheet ? '20px 20px 0 0' : '20px',
              boxShadow: '0 20px 60px rgba(15,23,42,0.30)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'orFilterPanelIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {/* Poignée (mobile) */}
            {bottomSheet && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
                <div style={{ width: 40, height: 4, borderRadius: 999, backgroundColor: '#cbd5e1' }} />
              </div>
            )}

            {/* En-tête */}
            <div style={{
              padding: bottomSheet ? '12px 20px 16px' : '20px 22px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              borderBottom: '1px solid #eef2f7'
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                flexShrink: 0
              }}>
                <FunnelIcon size={20} />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Filtres</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {hasActive
                    ? `${activeFiltersCount} filtre${activeFiltersCount > 1 ? 's' : ''} actif${activeFiltersCount > 1 ? 's' : ''} · ${resultsCount} OR`
                    : `Affinez la liste · ${resultsCount} OR`}
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Fermer"
                style={{
                  border: 'none',
                  background: '#f1f5f9',
                  borderRadius: 10,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#475569',
                  flexShrink: 0
                }}
              >
                <CloseIcon size={18} />
              </button>
            </div>

            {/* Corps : contrôles empilés en pleine largeur */}
            <div
              className="or-filter-body"
              style={{
                padding: '18px 22px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                flex: 1
              }}
            >
              {children}
            </div>

            {/* Pied : réinitialiser + valider */}
            <div style={{
              padding: '14px 22px',
              borderTop: '1px solid #eef2f7',
              display: 'flex',
              gap: 12,
              backgroundColor: '#fbfcfe'
            }}>
              <button
                onClick={onReset}
                disabled={!hasActive}
                style={{
                  flex: '0 0 auto',
                  padding: '11px 18px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  backgroundColor: 'white',
                  color: hasActive ? '#475569' : '#cbd5e1',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: hasActive ? 'pointer' : 'not-allowed'
                }}
              >
                Réinitialiser
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '11px 18px',
                  border: 'none',
                  borderRadius: 10,
                  backgroundColor: '#2563eb',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(37,99,235,0.35)'
                }}
              >
                Voir {resultsCount} résultat{resultsCount > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InterventionsPage({ refreshTrigger = 0 }: InterventionsPageProps) {
  const { isPhone, isDesktop } = useViewport();

  // RBAC : permissions
  const canView = useHasPermission('interventions.view');
  // À rebrancher quand les fonctionnalités correspondantes arriveront :
  //   useHasPermission('interventions.create') → création standalone
  //   useHasPermission('interventions.update') → modification statut / compte-rendu
  const canArchive = useHasPermission('interventions.archive');
  const canUnarchive = useHasPermission('interventions.unarchive');

  const [interventions, setInterventions] = useState<InterventionDetail[]>([]);
  const [total, setTotal] = useState(0);
  // Getter non lu : seules les cartes de résumé consomment ces chiffres, via `total` et les
  // compteurs derives. Le setter reste alimente par le chargement serveur.
  const [, setStatistiques] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [detailModal, setDetailModal] = useState<{ show: boolean; intervention: InterventionDetail | null }>({ show: false, intervention: null });
  const [archiveModal, setArchiveModal] = useState<{ show: boolean; intervention: InterventionDetail | null }>({ show: false, intervention: null });
  const [archiving, setArchiving] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });

  // État pour le modal des passages (historique)
  const [passagesModal, setPassagesModal] = useState<{ show: boolean; intervention: InterventionDetail | null }>({ show: false, intervention: null });
  const [passagesLoading, setPassagesLoading] = useState(false);
  const [passagesData, setPassagesData] = useState<PassagesData | null>(null);

  // État pour le mapping des statuts (code → libellé/couleur)
  const [statutsMapping, setStatutsMapping] = useState<Record<string, { libelle: string; color: string; app_status: string | null }>>({});

  // État pour le lightbox photo
  const [lightboxPhoto, setLightboxPhoto] = useState<PassagePhoto | null>(null);

  // Filtre par carte (Actives, En attente, En cours, Archivées)
  const [cardFilter, setCardFilter] = useState<'all' | 'active' | 'waiting' | 'in_progress' | 'archived'>('active');

  // Filtres pour le tableau des interventions
  const [searchTerm, setSearchTerm] = useState('');
  // Ref miroir du terme de recherche : permet a fetchDetail (deps []) d'envoyer
  // toujours le terme courant au serveur sans etre recree a chaque frappe.
  const searchTermRef = React.useRef('');
  const [filterTechnicien, setFilterTechnicien] = useState<string[]>([]);
  const [filterStatutCode, setFilterStatutCode] = useState<string[]>([]);
  // Par défaut : afficher uniquement les non-archivées
  const [filterArchived, setFilterArchived] = useState<string[]>(['false']);
  const [technicienDropdownOpen, setTechnicienDropdownOpen] = useState(false);
  const [statutCodeDropdownOpen, setStatutCodeDropdownOpen] = useState(false);
  const [archivedDropdownOpen, setArchivedDropdownOpen] = useState(false);
  const [technicienSearch, setTechnicienSearch] = useState('');

  // Nouveaux filtres : secteur / origine / atelier (multi-select) + plage de dates
  const [filterSecteur, setFilterSecteur] = useState<string[]>([]);
  const [filterOrigine, setFilterOrigine] = useState<string[]>([]);
  const [filterAtelier, setFilterAtelier] = useState<string[]>([]);
  // Un seul des 3 nouveaux dropdowns ouvert à la fois
  const [openFilter, setOpenFilter] = useState<'secteur' | 'origine' | 'atelier' | null>(null);
  // Plage de dates : base (planifiée / émission) + bornes (YYYY-MM-DD)
  const [filterDateType, setFilterDateType] = useState<'planifiee' | 'emission'>('planifiee');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');

  // Modale de filtres (affichée à la place de la barre inline hors desktop)
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  // Réinitialise tous les filtres de la barre (les cartes de résumé ne sont pas concernées)
  const resetAllFilters = () => {
    setSearchTerm('');
    setFilterTechnicien([]);
    // Vide aussi le champ de recherche interne du dropdown Technicien : sans cela, le texte
    // tapé survivait au reset et le dropdown se rouvrait filtré sur une saisie fantôme.
    setTechnicienSearch('');
    setFilterStatutCode([]);
    setFilterSecteur([]);
    setFilterOrigine([]);
    setFilterAtelier([]);
    setFilterDateType('planifiee');
    setFilterDateDebut('');
    setFilterDateFin('');
    setFilterArchived(['false']);
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = localStorage.getItem('servup_or_pageSize');
    return saved ? parseInt(saved, 10) : 20;
  });

  // Reset page quand filtres ou recherche changent
  React.useEffect(() => { searchTermRef.current = searchTerm; setCurrentPage(1); }, [searchTerm, filterTechnicien, filterStatutCode, filterArchived, cardFilter, filterSecteur, filterOrigine, filterAtelier, filterDateDebut, filterDateFin, filterDateType]);

  // ================================================================
  // Colonnes configurables (ordre + taille modifiables, persistées)
  // ================================================================

  interface ColumnDef {
    id: string;
    label: string;
    width: number;
    minWidth: number;
    title?: string;
    align?: 'left' | 'center' | 'right';
  }

  const defaultColumns: ColumnDef[] = [
    { id: 'numero',     label: 'N° OR',     width: 65,  minWidth: 50 },
    { id: 'emission',   label: 'Émission',   width: 78,  minWidth: 60, title: "Date d'émission de l'intervention" },
    { id: 'planifiee',  label: 'Planifiée',  width: 78,  minWidth: 60, title: "Date prévue de l'intervention" },
    { id: 'client',     label: 'Client',     width: 180, minWidth: 100 },
    { id: 'secteur',    label: 'Secteur',    width: 100, minWidth: 60, title: 'Secteur géographique' },
    { id: 'qte',        label: 'Qté',        width: 40,  minWidth: 30, align: 'center', title: 'Quantité planifiée / réalisée' },
    { id: 'technicien', label: 'Technicien', width: 150, minWidth: 80 },
    { id: 'statut',     label: 'Statut',     width: 150, minWidth: 80 },
    { id: 'int',        label: 'INT.',       width: 44,  minWidth: 30, align: 'center', title: "Nombre d'interventions (passages)" },
    { id: 'pcs',        label: 'PCS',        width: 44,  minWidth: 30, align: 'center', title: 'Nombre de pièces utilisées' },
    { id: 'enc',        label: 'ENC.',       width: 55,  minWidth: 40, align: 'center', title: 'Total encaissé en euros' },
  ];

  const STORAGE_KEY_ORDER = 'servup_or_columnOrder';
  const STORAGE_KEY_WIDTHS = 'servup_or_columnWidths';
  const ACTIONS_WIDTH = 84;

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_ORDER);
      if (saved) {
        const savedOrder: string[] = JSON.parse(saved);
        const defaultIds = defaultColumns.map(c => c.id);
        // Ajouter les nouvelles colonnes à leur position par défaut
        const missing = defaultIds.filter(id => !savedOrder.includes(id));
        if (missing.length > 0) {
          const merged = [...savedOrder];
          missing.forEach(id => {
            const defIdx = defaultIds.indexOf(id);
            let insertIdx = merged.length;
            for (let i = defIdx + 1; i < defaultIds.length; i++) {
              const pos = merged.indexOf(defaultIds[i]);
              if (pos !== -1) { insertIdx = pos; break; }
            }
            merged.splice(insertIdx, 0, id);
          });
          return merged.filter(id => defaultIds.includes(id));
        }
        return savedOrder.filter(id => defaultIds.includes(id));
      }
    } catch {}
    return defaultColumns.map(c => c.id);
  });

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const defaults: Record<string, number> = {};
    defaultColumns.forEach(c => { defaults[c.id] = c.width; });
    try {
      const saved = localStorage.getItem(STORAGE_KEY_WIDTHS);
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {}
    return defaults;
  });

  // Drag & drop (réordonnement)
  const [draggedCol, setDraggedCol] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  // Empêche un clic parasite (donc un tri) juste après un drag de réordonnement
  const justDraggedRef = React.useRef(false);

  // Resize
  const [resizingCol, setResizingCol] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartW, setResizeStartW] = useState(0);

  // Tri par colonne (clic sur l'en-tête). null = ordre par défaut (date desc).
  const [sortColumn, setSortColumn] = useState<string | null>(() => localStorage.getItem('servup_or_sortCol') || null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const saved = localStorage.getItem('servup_or_sortDir');
    return saved === 'desc' ? 'desc' : 'asc';
  });

  useEffect(() => {
    if (sortColumn) localStorage.setItem('servup_or_sortCol', sortColumn);
    else localStorage.removeItem('servup_or_sortCol');
    localStorage.setItem('servup_or_sortDir', sortDirection);
  }, [sortColumn, sortDirection]);

  // Clic sur en-tête : asc → desc → défaut (3 états)
  const handleSort = (colId: string) => {
    if (justDraggedRef.current) return; // ignore le clic issu d'un drag de réordonnement
    if (sortColumn !== colId) {
      setSortColumn(colId);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumn(null);
      setSortDirection('asc');
    }
  };

  // Persistance
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_WIDTHS, JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Drag handlers
  const handleColDragStart = (e: React.DragEvent, colId: string) => {
    setDraggedCol(colId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleColDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedCol && draggedCol !== colId) setDragOverCol(colId);
  };
  const handleColDrop = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    if (!draggedCol || draggedCol === colId) { setDraggedCol(null); setDragOverCol(null); return; }
    const newOrder = [...columnOrder];
    const fromIdx = newOrder.indexOf(draggedCol);
    const toIdx = newOrder.indexOf(colId);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedCol);
    setColumnOrder(newOrder);
    setDraggedCol(null);
    setDragOverCol(null);
  };
  const handleColDragEnd = () => {
    justDraggedRef.current = true;
    setTimeout(() => { justDraggedRef.current = false; }, 0);
    setDraggedCol(null);
    setDragOverCol(null);
  };

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingCol(colId);
    setResizeStartX(e.clientX);
    setResizeStartW(columnWidths[colId] || defaultColumns.find(c => c.id === colId)!.width);
  };

  useEffect(() => {
    if (!resizingCol) return;
    const col = defaultColumns.find(c => c.id === resizingCol);
    const minW = col?.minWidth || 30;
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX;
      const newW = Math.max(minW, Math.min(500, resizeStartW + delta));
      setColumnWidths(prev => ({ ...prev, [resizingCol]: newW }));
    };
    const handleMouseUp = () => setResizingCol(null);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingCol, resizeStartX, resizeStartW]);

  // Colonnes ordonnées avec largeurs courantes
  const getOrderedColumns = (): ColumnDef[] =>
    columnOrder
      .map(id => defaultColumns.find(c => c.id === id))
      .filter((c): c is ColumnDef => !!c)
      .map(c => ({ ...c, width: columnWidths[c.id] || c.width }));

  const getGridTemplate = (): string =>
    `minmax(${ACTIONS_WIDTH}px, ${ACTIONS_WIDTH}px) ` + getOrderedColumns().map(c => `minmax(${c.minWidth}px, ${c.width}fr)`).join(' ');

  // Afficher un toast temporaire
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 4000);
  };

  // Récupérer le token SSO
  const getToken = () => localStorage.getItem('sso_token');

  // ================================================================
  // Charger les interventions depuis l'API v3
  // GET /api/interventions?includeArchived=true
  // ================================================================
  const fetchDetail = useCallback(async (notify: boolean = false, searchValue?: string) => {
    const token = getToken();
    if (!token) {
      setError('Session expirée');
      setLoading(false);
      return;
    }

    try {
      console.log('[InterventionsPage] Chargement des interventions...');

      // Recherche serveur : si un terme est saisi, on interroge TOUTE la base
      // (et non plus seulement les 500 lignes chargees cote client).
      const effectiveSearch = (searchValue !== undefined ? searchValue : searchTermRef.current).trim();
      const searchQs = effectiveSearch ? `&search=${encodeURIComponent(effectiveSearch)}` : '';
      const response = await fetch(`${API_BASE}/api/interventions?includeArchived=true&limit=500${searchQs}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result: ApiResponse = await response.json();
      console.log('[InterventionsPage] Données reçues:', result.count, 'interventions sur', result.total);

      if (result.success) {
        setInterventions(result.interventions || []);
        setTotal(result.total || 0);
        setStatistiques(result.statistiques?.par_code_statut || {});
        setError(null);

        if (notify) showToast('success', 'Données actualisées');
      } else {
        throw new Error('Erreur inconnue');
      }

      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('[InterventionsPage] Erreur:', err);
      setError(err.message);
      if (notify) showToast('error', `Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger le mapping des statuts
  const fetchStatuts = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      console.log('[InterventionsPage] Chargement des statuts...');
      const response = await fetch(`${API_BASE}/api/statuts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.statuts) {
          const mapping: Record<string, { libelle: string; color: string; app_status: string | null }> = {};
          result.statuts.forEach((s: { code: string; label: string; libelle?: string; color: string; app_status?: string }) => {
            mapping[s.code] = { libelle: s.label || s.libelle || '', color: s.color, app_status: s.app_status || null };
          });
          setStatutsMapping(mapping);
          console.log('[InterventionsPage] Statuts mapping chargé:', Object.keys(mapping).length, 'statuts');
        }
      }
    } catch (err) {
      console.error('[InterventionsPage] Erreur chargement statuts:', err);
    }
  }, []);

  // Récupérer les passages d'une intervention
  const fetchPassages = useCallback(async (interventionId: string) => {
    const token = getToken();
    if (!token) return;

    setPassagesLoading(true);
    setPassagesData(null);

    try {
      console.log('[InterventionsPage] Chargement passages pour:', interventionId);
      const response = await fetch(`${API_BASE}/api/interventions/${interventionId}/passages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[InterventionsPage] Passages reçus:', result.data?.passages?.length || 0);
        setPassagesData(result.data || null);
      } else {
        console.error('[InterventionsPage] Erreur récupération passages:', response.status);
      }
    } catch (err) {
      console.error('[InterventionsPage] Erreur fetch passages:', err);
    } finally {
      setPassagesLoading(false);
    }
  }, []);

  // Charger les passages quand le modal s'ouvre
  React.useEffect(() => {
    if (passagesModal.show && passagesModal.intervention) {
      const id = passagesModal.intervention.id || passagesModal.intervention.external_id;
      if (id) fetchPassages(id);
    } else {
      setPassagesData(null);
    }
  }, [passagesModal.show, passagesModal.intervention, fetchPassages]);

  // Helper pour obtenir le libellé d'un statut
  const getStatutLibelle = (code: string | null): string => {
    if (!code) return '-';
    return statutsMapping[code]?.libelle || `Code ${code}`;
  };

  // ================================================================
  // Archiver une intervention
  // POST /api/interventions/{id}/archive (UUID)
  // ================================================================
  const archiveIntervention = async (id: string, reason: string = 'manual') => {
    const token = getToken();
    if (!token) return;

    setArchiving(true);
    setError(null);

    try {
      console.log('[InterventionsPage] Archivage intervention:', id);

      const response = await fetch(`${API_BASE}/api/interventions/${id}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error?.message || `Erreur HTTP ${response.status}`);
      }

      console.log('[InterventionsPage] Intervention archivée');
      showToast('success', `Intervention ${archiveModal.intervention?.numero_ordre || id} archivée`);

      // Rafraîchir les données
      await fetchDetail();
      setArchiveModal({ show: false, intervention: null });
    } catch (err: any) {
      console.error('[InterventionsPage] Erreur archivage:', err);
      setError(err.message);
      showToast('error', `Erreur: ${err.message}`);
    } finally {
      setArchiving(false);
    }
  };

  // ================================================================
  // Désarchiver une intervention
  // POST /api/interventions/{id}/unarchive (UUID)
  // ================================================================
  const unarchiveIntervention = async (id: string) => {
    const token = getToken();
    if (!token) return;

    setArchiving(true);
    setError(null);

    try {
      console.log('[InterventionsPage] Désarchivage intervention:', id);

      const response = await fetch(`${API_BASE}/api/interventions/${id}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error?.message || `Erreur HTTP ${response.status}`);
      }

      console.log('[InterventionsPage] Intervention désarchivée');
      showToast('success', `Intervention ${id} désarchivée`);

      // Rafraîchir les données
      await fetchDetail();
    } catch (err: any) {
      console.error('[InterventionsPage] Erreur désarchivage:', err);
      setError(err.message);
      showToast('error', `Erreur: ${err.message}`);
    } finally {
      setArchiving(false);
    }
  };

  // Charger au montage
  useEffect(() => {
    fetchDetail();
    fetchStatuts();
  }, [fetchDetail, fetchStatuts]);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    console.log('[InterventionsPage] refreshTrigger changed, refetching...');
    fetchDetail(false);
  }, [refreshTrigger, fetchDetail]);

  // Recherche serveur : refetch (debounce 350 ms) quand le terme de recherche change.
  // Le 1er rendu est ignore (le chargement initial est gere par l'effet de montage).
  const isFirstSearch = React.useRef(true);
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const handle = setTimeout(() => {
      console.log('[InterventionsPage] Recherche serveur:', searchTerm);
      fetchDetail(false, searchTerm);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchTerm, fetchDetail]);

  // Formater la date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formater la date courte (JJ/MM/AAAA)
  const formatDateShort = (dateStr: string | null | undefined) => {
    if (!dateStr || dateStr === '-') return '-';
    if (dateStr.length === 8) {
      return `${dateStr.slice(6, 8)}/${dateStr.slice(4, 6)}/${dateStr.slice(0, 4)}`;
    }
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    return dateStr;
  };

  // Fermer les dropdowns quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.multi-select-dropdown')) {
        setTechnicienDropdownOpen(false);
        setStatutCodeDropdownOpen(false);
        setArchivedDropdownOpen(false);
        setOpenFilter(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Liste des techniciens uniques
  const techniciensList = interventions
    ? [...new Set(interventions.map(i => i.technicien?.nom).filter(Boolean))].sort() as string[]
    : [];

  // Options dynamiques secteur / origine / atelier (valeurs présentes dans les interventions chargées)
  const uniqueSorted = (values: (string | null | undefined)[]): string[] =>
    [...new Set(values.map(v => v?.trim()).filter(Boolean) as string[])]
      .sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }));

  const secteurOptions = React.useMemo(
    () => uniqueSorted(interventions.map(i => i.quotas?.secteur)),
    [interventions]
  );
  const origineOptions = React.useMemo(
    () => uniqueSorted(interventions.map(i => i.intervention?.origine)),
    [interventions]
  );
  const atelierOptions = React.useMemo(
    () => uniqueSorted(interventions.map(i => i.intervention?.atelier)),
    [interventions]
  );

  // Options de statut dynamiques (uniquement ceux présents dans les interventions)
  const statutCodeOptions = React.useMemo(() => {
    const codesInList = new Set(interventions.map(i => i.statut?.code).filter(Boolean) as string[]);
    return [...codesInList]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(code => ({
        value: code,
        label: `${code} ${statutsMapping[code]?.libelle || ''}`.trim(),
        color: statutsMapping[code]?.color || '#6B7280'
      }));
  }, [interventions, statutsMapping]);

  // Options archivé
  const archivedOptions = [
    { value: 'false', label: 'Non archivé', color: '#22c55e' },
    { value: 'true', label: 'Archivé', color: '#ef4444' }
  ];

  // Compter pour les cartes
  const archivedCount = interventions.filter(i => i.archivage?.is_archived).length;
  const activeCount = interventions.filter(i => !i.archivage?.is_archived).length;
  const waitingCount = interventions.filter(i => !i.archivage?.is_archived && ['pending', 'scheduled', 'on_hold'].includes(i.statut?.app_status)).length;
  const inProgressCount = interventions.filter(i => !i.archivage?.is_archived && i.statut?.app_status === 'in_progress').length;

  // Normalise une date (ISO 'YYYY-MM-DD' ou legacy 'YYYYMMDD') en 'YYYY-MM-DD' comparable
  const toISODate = (d: string | null | undefined): string | null => {
    if (!d) return null;
    const s = String(d).trim();
    if (!s) return null;
    if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0, 10);
    return s;
  };

  // Filtrer les interventions
  const filteredInterventions = interventions.filter(intervention => {
    // Filtre recherche
    const matchSearch = searchTerm === '' ||
      intervention.numero_ordre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.client?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.appareil?.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.external_id?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtre technicien
    const matchTechnicien = filterTechnicien.length === 0 ||
      filterTechnicien.includes(intervention.technicien?.nom || '');

    // Filtre statut AS400 (code)
    const matchStatutCode = filterStatutCode.length === 0 ||
      filterStatutCode.includes(intervention.statut?.code || '');

    // Filtre secteur (quota_sector)
    const matchSecteur = filterSecteur.length === 0 ||
      filterSecteur.includes((intervention.quotas?.secteur || '').trim());

    // Filtre origine
    const matchOrigine = filterOrigine.length === 0 ||
      filterOrigine.includes((intervention.intervention?.origine || '').trim());

    // Filtre atelier
    const matchAtelier = filterAtelier.length === 0 ||
      filterAtelier.includes((intervention.intervention?.atelier || '').trim());

    // Filtre plage de dates (base : planifiée ou émission)
    let matchDate = true;
    if (filterDateDebut || filterDateFin) {
      const raw = filterDateType === 'emission'
        ? intervention.dates?.emission
        : intervention.date_intervention;
      const iso = toISODate(raw);
      if (!iso) {
        matchDate = false;
      } else {
        if (filterDateDebut && iso < filterDateDebut) matchDate = false;
        if (filterDateFin && iso > filterDateFin) matchDate = false;
      }
    }

    // Cartes et menu « Archivé » — une recherche par numéro passe outre (utils/filtreCartes.ts)
    const matchCard = passeLaCarte({
      carte: cardFilter,
      filtreArchive: filterArchived,
      recherche: searchTerm,
      archivee: intervention.archivage?.is_archived || false,
      appStatus: intervention.statut?.app_status,
    });

    return matchSearch && matchTechnicien && matchStatutCode && matchSecteur && matchOrigine && matchAtelier && matchDate && matchCard;
  });

  // Compter les filtres actifs
  const activeFiltersCount = [
    filterTechnicien.length > 0,
    filterStatutCode.length > 0,
    filterSecteur.length > 0,
    filterOrigine.length > 0,
    filterAtelier.length > 0,
    Boolean(filterDateDebut || filterDateFin),
    filterArchived.length > 0 && !(filterArchived.length === 1 && filterArchived[0] === 'false') // Ne pas compter le filtre par défaut
  ].filter(Boolean).length;

  // Le bouton de réinitialisation couvre un peu plus que le badge : la recherche et un état
  // « Archivé » non-défaut ne sont pas comptés comme filtres actifs, mais sont bien remis à
  // zéro par resetAllFilters(). Sans ce prédicat, une recherche seule n'offrirait aucun bouton.
  const canResetFilters =
    activeFiltersCount > 0 ||
    searchTerm.trim() !== '' ||
    !(filterArchived.length === 1 && filterArchived[0] === 'false');

  // Valeur triable pour une colonne donnée (number pour les colonnes numériques/dates, string sinon)
  const getSortValue = (i: InterventionDetail, colId: string): string | number | null => {
    switch (colId) {
      case 'numero': {
        const raw = i.numero_ordre ?? i.external_id;
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        return Number.isNaN(n) ? String(raw) : n;
      }
      case 'emission':   return toISODate(i.dates?.emission);
      case 'planifiee':  return toISODate(i.date_intervention);
      case 'client':     return (i.client?.nom || '').trim().toLowerCase() || null;
      case 'secteur':    return (i.quotas?.secteur || '').trim().toLowerCase() || null;
      case 'qte':        return i.quotas?.quantite_planifiee != null ? Number(i.quotas.quantite_planifiee) : null;
      case 'technicien': return (i.technicien?.nom || '').trim().toLowerCase() || null;
      case 'statut':     return (i.statut?.code || '').trim() || null;
      case 'int':        return i.totaux?.passages_count != null ? Number(i.totaux.passages_count) : null;
      case 'pcs':        return i.totaux?.pieces_count != null ? Number(i.totaux.pieces_count) : null;
      case 'enc':        return i.totaux?.payments_total != null ? Number(i.totaux.payments_total) : null;
      default:           return null;
    }
  };

  // Liste triée (les valeurs vides finissent toujours en dernier, quel que soit le sens)
  const sortedInterventions = sortColumn
    ? [...filteredInterventions].sort((a, b) => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        const va = getSortValue(a, sortColumn);
        const vb = getSortValue(b, sortColumn);
        const aEmpty = va === null || va === undefined || va === '';
        const bEmpty = vb === null || vb === undefined || vb === '';
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), 'fr', { numeric: true, sensitivity: 'base' }) * dir;
      })
    : filteredInterventions;

  // Pagination : calcul des pages
  const totalPages = Math.max(1, Math.ceil(sortedInterventions.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedInterventions = sortedInterventions.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Rendre la liste des interventions
  const renderInterventionsList = (items: InterventionDetail[]) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      maxHeight: 'calc(100vh - 280px)',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {/* En-tête tableau (colonnes réordonnables + redimensionnables) — sticky */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: getGridTemplate(),
        columnGap: '8px',
        padding: '0 16px',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        fontSize: '11px',
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        userSelect: resizingCol ? 'none' : undefined,
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        {/* Actions (fixe, non déplaçable) */}
        <div style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</div>
        {/* Colonnes configurables */}
        {getOrderedColumns().map(col => {
          const isDragging = draggedCol === col.id;
          const isDropTarget = dragOverCol === col.id;
          return (
            <div
              key={col.id}
              draggable
              onDragStart={(e) => handleColDragStart(e, col.id)}
              onDragOver={(e) => handleColDragOver(e, col.id)}
              onDrop={(e) => handleColDrop(e, col.id)}
              onDragEnd={handleColDragEnd}
              onClick={() => handleSort(col.id)}
              title={`${col.title || col.label} — cliquer pour trier`}
              style={{
                position: 'relative',
                padding: '12px 8px',
                cursor: isDragging ? 'grabbing' : 'grab',
                opacity: isDragging ? 0.5 : 1,
                backgroundColor: isDragging ? '#d0d0d0' : isDropTarget ? '#b3d7ff' : 'transparent',
                borderLeft: isDropTarget ? '3px solid #3b82f6' : '0px solid transparent',
                textAlign: col.align || 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.15s, border-left 0.15s'
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', verticalAlign: 'middle' }}>
                {col.label}
                <span style={{
                  fontSize: '9px',
                  color: sortColumn === col.id ? '#3b82f6' : '#cbd5e1',
                  lineHeight: 1
                }}>
                  {sortColumn === col.id ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                </span>
              </span>
              {/* Resize handle */}
              <div
                onMouseDown={(e) => handleResizeStart(e, col.id)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '6px',
                  cursor: 'col-resize',
                  backgroundColor: resizingCol === col.id ? '#3b82f6' : 'transparent',
                  borderRight: '1px solid #e2e8f0',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                onMouseLeave={(e) => { if (resizingCol !== col.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
              />
            </div>
          );
        })}
      </div>

      {/* Lignes */}
      {items.map((intervention, index) => (
        <div
          key={intervention.id || index}
          style={{
            display: 'grid',
            gridTemplateColumns: getGridTemplate(),
            columnGap: '8px',
            padding: '0 16px',
            borderBottom: index < items.length - 1 ? '1px solid #f1f5f9' : 'none',
            fontSize: '13px',
            alignItems: 'center'
          }}
        >
          {/* Actions (fixe) */}
          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', padding: '12px 8px' }}>
            <button
              onClick={() => setDetailModal({ show: true, intervention })}
              style={{
                padding: '5px',
                backgroundColor: 'transparent',
                border: '1px solid #93c5fd',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#93c5fd';
              }}
              title="Voir le détail complet"
            >
              <EyeIcon size={14} color="#3b82f6" />
            </button>
            <button
              onClick={() => setPassagesModal({ show: true, intervention })}
              style={{
                padding: '5px',
                backgroundColor: 'transparent',
                border: '1px solid #c4b5fd',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f3ff';
                e.currentTarget.style.borderColor = '#8b5cf6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#c4b5fd';
              }}
              title="Voir l'historique des interventions"
            >
              <HistoryIcon size={14} color="#8b5cf6" />
            </button>
            {!intervention.archivage?.is_archived ? (
              canArchive && (
                <button
                  onClick={() => setArchiveModal({ show: true, intervention })}
                  style={{
                    padding: '5px',
                    backgroundColor: 'transparent',
                    border: '1px solid #86efac',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0fdf4';
                    e.currentTarget.style.borderColor = '#22c55e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#86efac';
                  }}
                  title="Archiver l'intervention"
                >
                  <ArchiveIcon size={14} color="#22c55e" />
                </button>
              )
            ) : (
              canUnarchive && (
                <button
                  onClick={() => unarchiveIntervention(intervention.id)}
                  disabled={archiving}
                  style={{
                    padding: '5px',
                    backgroundColor: 'transparent',
                    border: '1px solid #fca5a5',
                    borderRadius: '4px',
                    cursor: archiving ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                    e.currentTarget.style.borderColor = '#ef4444';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#fca5a5';
                  }}
                  title="Désarchiver l'intervention"
                >
                  <ArchiveIcon size={14} color="#ef4444" />
                </button>
              )
            )}
          </div>

          {/* Cellules dynamiques selon l'ordre des colonnes */}
          {getOrderedColumns().map(col => (
            <div key={col.id} style={{ padding: '12px 8px', overflow: 'hidden' }}>
              {col.id === 'numero' && (
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>
                  {intervention.numero_ordre || intervention.external_id || '-'}
                </div>
              )}
              {col.id === 'emission' && (
                <div style={{ fontSize: '12px', color: '#6366f1', fontWeight: 500 }}>
                  {formatDateShort(intervention.dates?.emission)}
                </div>
              )}
              {col.id === 'planifiee' && (
                <div style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>
                  {formatDateShort(intervention.date_intervention)}
                </div>
              )}
              {col.id === 'client' && (
                <div>
                  <div style={{ color: '#1e293b', fontWeight: 500, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {intervention.client?.nom || '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {intervention.client?.code_postal} {intervention.client?.ville}
                  </div>
                </div>
              )}
              {col.id === 'secteur' && (
                <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={intervention.quotas?.secteur || '-'}>
                  {intervention.quotas?.secteur || '-'}
                </div>
              )}
              {col.id === 'qte' && (
                <div style={{ fontSize: '12px', textAlign: 'center', fontWeight: 600, color: Number(intervention.quotas?.quantite_planifiee) > 0 ? '#1e293b' : '#94a3b8' }}>
                  {intervention.quotas?.quantite_planifiee != null ? Number(intervention.quotas.quantite_planifiee) : '-'}
                </div>
              )}
              {col.id === 'technicien' && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 500, lineHeight: 1.3 }}>
                    {intervention.technicien?.nom || '-'}
                  </span>
                  {intervention.technicien?.code && (
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: '#e0e7ff',
                      color: '#4338ca',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}>
                      {intervention.technicien.code}
                    </span>
                  )}
                </div>
              )}
              {col.id === 'statut' && (() => {
                const code = intervention.statut?.code || null;
                const mapped = code ? statutsMapping[code] : null;
                const isMapped = mapped?.app_status && mapped.app_status !== 'ignored';
                const badgeColor = mapped?.color || '#6B7280';
                return (
                  <div style={{ fontSize: '11px' }} title={`${code || '-'} - ${intervention.statut?.libelle || '-'}`}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      ...(isMapped ? {
                        backgroundColor: `${badgeColor}18`,
                        color: badgeColor,
                      } : {
                        backgroundColor: 'transparent',
                        border: '1px solid #374151',
                        color: '#374151',
                      }),
                      fontWeight: 600,
                      fontSize: '11px'
                    }}>
                      <span style={{ fontWeight: 700 }}>{code || '-'}</span>
                      <span style={{ fontWeight: 400 }}>{intervention.statut?.libelle || '-'}</span>
                    </span>
                  </div>
                );
              })()}
              {col.id === 'int' && (
                <div style={{ textAlign: 'center' }}>
                  {intervention.totaux?.passages_count && intervention.totaux.passages_count > 0 ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: '24px', height: '24px', padding: '0 6px',
                      backgroundColor: '#dbeafe', color: '#1d4ed8',
                      borderRadius: '12px', fontWeight: 600, fontSize: '12px'
                    }}>
                      {intervention.totaux.passages_count}
                    </span>
                  ) : (
                    <span style={{ color: '#cbd5e1', fontSize: '12px' }}>-</span>
                  )}
                </div>
              )}
              {col.id === 'pcs' && (
                <div style={{ textAlign: 'center' }}>
                  {intervention.totaux?.pieces_count && intervention.totaux.pieces_count > 0 ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: '24px', height: '24px', padding: '0 6px',
                      backgroundColor: '#fef3c7', color: '#92400e',
                      borderRadius: '12px', fontWeight: 600, fontSize: '12px'
                    }}>
                      {intervention.totaux.pieces_count}
                    </span>
                  ) : (
                    <span style={{ color: '#cbd5e1', fontSize: '12px' }}>-</span>
                  )}
                </div>
              )}
              {col.id === 'enc' && (
                <div style={{ textAlign: 'center', fontSize: '12px' }}>
                  {intervention.totaux?.payments_total && intervention.totaux.payments_total > 0 ? (
                    <span style={{ color: '#166534', fontWeight: 600 }}>
                      {intervention.totaux.payments_total.toFixed(0)} €
                    </span>
                  ) : (
                    <span style={{ color: '#cbd5e1' }}>-</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {items.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
          Aucune intervention correspondant aux filtres
        </div>
      )}
    </div>
  );

  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <RefreshIcon size={32} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '12px', color: '#64748b' }}>Chargement des interventions...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', position: 'relative' }}>
      {/* Toast notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 20px',
          backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#86efac' : '#fecaca'}`,
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease'
        }}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: toast.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {toast.type === 'success' ? '✓' : '✕'}
          </span>
          <span style={{
            color: toast.type === 'success' ? '#166534' : '#dc2626',
            fontWeight: 500,
            fontSize: '14px'
          }}>
            {toast.message}
          </span>
          <button
            onClick={() => setToast({ ...toast, show: false })}
            style={{
              marginLeft: '8px',
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: '18px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header avec bouton Actualiser */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Total: <strong style={{ color: '#1e293b' }}>{total} intervention{total > 1 ? 's' : ''}</strong>
          </div>
          {lastRefresh && (
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
              Dernière actualisation: {formatDate(lastRefresh.toISOString())}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => fetchDetail(true)}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.borderColor = '#94a3b8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            <RefreshIcon size={18} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CloseIcon size={18} />
          {error}
        </div>
      )}

      {interventions.length > 0 || !loading ? (
        <>
          {/* Résumé (cliquables = filtres) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isPhone ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isPhone ? '12px' : '16px',
            marginBottom: '24px'
          }}>
            {[
              { key: 'active' as const, value: activeCount, label: 'Actives', bg: '#dbeafe', accent: '#1e40af' },
              { key: 'waiting' as const, value: waitingCount, label: 'En attente / Planifiées', bg: '#fef3c7', accent: '#f59e0b' },
              { key: 'in_progress' as const, value: inProgressCount, label: 'En cours', bg: '#dcfce7', accent: '#22c55e' },
              { key: 'archived' as const, value: archivedCount, label: 'Archivées', bg: '#fae8ff', accent: '#86198f' }
            ].map(card => {
              const isActive = cardFilter === card.key;
              return (
                <div
                  key={card.key}
                  onClick={() => setCardFilter(isActive ? 'all' : card.key)}
                  style={{
                    backgroundColor: card.bg,
                    padding: '16px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: isActive ? `2px solid ${card.accent}` : '2px solid transparent',
                    boxShadow: isActive ? `0 0 0 1px ${card.accent}20` : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ fontSize: '28px', fontWeight: 700, color: card.accent }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: '12px', color: card.accent, marginTop: '4px', fontWeight: isActive ? 600 : 400 }}>
                    {card.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filtres — barre inline (desktop) ou bouton + modale (mobile/tablette) */}
          <FilterBarResponsive
            isDesktop={isDesktop}
            isPhone={isPhone}
            activeFiltersCount={activeFiltersCount}
            canReset={canResetFilters}
            resultsCount={filteredInterventions.length}
            open={filtersModalOpen}
            onOpen={() => setFiltersModalOpen(true)}
            onClose={() => setFiltersModalOpen(false)}
            onReset={resetAllFilters}
          >
            {/* Recherche */}
            <div className="or-filter-search" style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '300px' }}>
              <SearchIcon size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Rechercher (N° OR, client, appareil)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px 9px 40px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Filtre Technicien */}
            <div className="multi-select-dropdown" style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTechnicienDropdownOpen(!technicienDropdownOpen);
                  setStatutCodeDropdownOpen(false);
                  setArchivedDropdownOpen(false);
                  setOpenFilter(null);
                }}
                style={{
                  padding: '9px 14px',
                  border: `1px solid ${filterTechnicien.length > 0 ? '#8b5cf6' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  backgroundColor: filterTechnicien.length > 0 ? '#f5f3ff' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '140px'
                }}
              >
                <span style={{ color: filterTechnicien.length > 0 ? '#7c3aed' : '#64748b' }}>
                  Technicien {filterTechnicien.length > 0 && `(${filterTechnicien.length})`}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
              </button>
              {technicienDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  minWidth: '220px',
                  maxHeight: '320px',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={technicienSearch}
                      onChange={(e) => setTechnicienSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 0' }}>
                    {techniciensList
                      .filter(t => t.toLowerCase().includes(technicienSearch.toLowerCase()))
                      .map(t => (
                        <label
                          key={t}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            backgroundColor: filterTechnicien.includes(t) ? '#f5f3ff' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterTechnicien.includes(t) ? '#f5f3ff' : 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={filterTechnicien.includes(t)}
                            onChange={() => {
                              setFilterTechnicien(prev =>
                                prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
                              );
                            }}
                            style={{ accentColor: '#8b5cf6' }}
                          />
                          <span style={{ color: '#374151' }}>{t}</span>
                        </label>
                      ))}
                  </div>
                  {filterTechnicien.length > 0 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                      <button
                        onClick={() => setFilterTechnicien([])}
                        style={{
                          width: '100%',
                          padding: '6px',
                          backgroundColor: '#f1f5f9',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        Effacer
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Filtre Statut */}
            <div className="multi-select-dropdown" style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setStatutCodeDropdownOpen(!statutCodeDropdownOpen);
                  setTechnicienDropdownOpen(false);
                  setArchivedDropdownOpen(false);
                  setOpenFilter(null);
                }}
                style={{
                  padding: '9px 14px',
                  border: `1px solid ${filterStatutCode.length > 0 ? '#3b82f6' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  backgroundColor: filterStatutCode.length > 0 ? '#eff6ff' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '130px'
                }}
              >
                <span style={{ color: filterStatutCode.length > 0 ? '#2563eb' : '#64748b' }}>
                  Statut {filterStatutCode.length > 0 && `(${filterStatutCode.length})`}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
              </button>
              {statutCodeDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  minWidth: '180px',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '4px 0' }}>
                    {statutCodeOptions.map(opt => (
                      <label
                        key={opt.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          backgroundColor: filterStatutCode.includes(opt.value) ? '#f8fafc' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterStatutCode.includes(opt.value) ? '#f8fafc' : 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={filterStatutCode.includes(opt.value)}
                          onChange={() => {
                            setFilterStatutCode(prev =>
                              prev.includes(opt.value) ? prev.filter(x => x !== opt.value) : [...prev, opt.value]
                            );
                          }}
                          style={{ accentColor: opt.color }}
                        />
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: opt.color
                          }} />
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  {filterStatutCode.length > 0 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                      <button
                        onClick={() => setFilterStatutCode([])}
                        style={{
                          width: '100%',
                          padding: '6px',
                          backgroundColor: '#f1f5f9',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        Effacer
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Filtre Secteur */}
            <ListFilterDropdown
              label="Secteur"
              accent="#0ea5e9"
              activeBg="#f0f9ff"
              options={secteurOptions}
              selected={filterSecteur}
              onToggleValue={(v) => setFilterSecteur(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
              onClear={() => setFilterSecteur([])}
              isOpen={openFilter === 'secteur'}
              onToggleOpen={() => {
                setOpenFilter(openFilter === 'secteur' ? null : 'secteur');
                setTechnicienDropdownOpen(false); setStatutCodeDropdownOpen(false); setArchivedDropdownOpen(false);
              }}
              searchable
              minWidth={120}
            />

            {/* Filtre Origine */}
            <ListFilterDropdown
              label="Origine"
              accent="#f59e0b"
              activeBg="#fffbeb"
              options={origineOptions}
              selected={filterOrigine}
              onToggleValue={(v) => setFilterOrigine(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
              onClear={() => setFilterOrigine([])}
              isOpen={openFilter === 'origine'}
              onToggleOpen={() => {
                setOpenFilter(openFilter === 'origine' ? null : 'origine');
                setTechnicienDropdownOpen(false); setStatutCodeDropdownOpen(false); setArchivedDropdownOpen(false);
              }}
              searchable
              minWidth={120}
            />

            {/* Filtre Atelier */}
            <ListFilterDropdown
              label="Atelier"
              accent="#14b8a6"
              activeBg="#f0fdfa"
              options={atelierOptions}
              selected={filterAtelier}
              onToggleValue={(v) => setFilterAtelier(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
              onClear={() => setFilterAtelier([])}
              isOpen={openFilter === 'atelier'}
              onToggleOpen={() => {
                setOpenFilter(openFilter === 'atelier' ? null : 'atelier');
                setTechnicienDropdownOpen(false); setStatutCodeDropdownOpen(false); setArchivedDropdownOpen(false);
              }}
              searchable
              minWidth={120}
            />

            {/* Filtre Plage de dates (base : planifiée ou émission) */}
            <div className="multi-select-dropdown" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 8px',
              border: `1px solid ${(filterDateDebut || filterDateFin) ? '#6366f1' : '#e2e8f0'}`,
              borderRadius: '8px',
              backgroundColor: (filterDateDebut || filterDateFin) ? '#eef2ff' : 'white'
            }}>
              <select
                value={filterDateType}
                onChange={(e) => setFilterDateType(e.target.value as 'planifiee' | 'emission')}
                title="Date de référence pour le filtre"
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '12px',
                  color: (filterDateDebut || filterDateFin) ? '#4f46e5' : '#475569',
                  cursor: 'pointer',
                  outline: 'none',
                  fontWeight: 600
                }}
              >
                <option value="planifiee">Planifiée</option>
                <option value="emission">Émission</option>
              </select>
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>du</span>
              <input
                type="date"
                value={filterDateDebut}
                max={filterDateFin || undefined}
                onChange={(e) => setFilterDateDebut(e.target.value)}
                style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px 6px', fontSize: '12px', outline: 'none' }}
              />
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>au</span>
              <input
                type="date"
                value={filterDateFin}
                min={filterDateDebut || undefined}
                onChange={(e) => setFilterDateFin(e.target.value)}
                style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px 6px', fontSize: '12px', outline: 'none' }}
              />
              {(filterDateDebut || filterDateFin) && (
                <button
                  onClick={() => { setFilterDateDebut(''); setFilterDateFin(''); }}
                  title="Effacer les dates"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '16px',
                    lineHeight: 1,
                    padding: '0 2px'
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Filtre Archivé - avec indicateur visuel si filtre actif */}
            <div className="multi-select-dropdown" style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setArchivedDropdownOpen(!archivedDropdownOpen);
                  setTechnicienDropdownOpen(false);
                  setStatutCodeDropdownOpen(false);
                  setOpenFilter(null);
                }}
                style={{
                  padding: '9px 14px',
                  border: `2px solid ${filterArchived.length > 0 ? '#22c55e' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  backgroundColor: filterArchived.length > 0 ? '#f0fdf4' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '160px'
                }}
              >
                {/* Indicateur de filtre actif */}
                {filterArchived.length > 0 && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    borderRadius: '50%',
                    fontSize: '10px',
                    fontWeight: 700
                  }}>
                    ✓
                  </span>
                )}
                <span style={{ color: filterArchived.length > 0 ? '#166534' : '#64748b', fontWeight: filterArchived.length > 0 ? 600 : 400 }}>
                  {filterArchived.length === 1 && filterArchived[0] === 'false' ? 'Non archivées' :
                   filterArchived.length === 1 && filterArchived[0] === 'true' ? 'Archivées' :
                   filterArchived.length === 2 ? 'Toutes' : 'Archivé'}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
              </button>
              {archivedDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  minWidth: '180px',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '4px 0' }}>
                    {archivedOptions.map(opt => (
                      <label
                        key={opt.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          backgroundColor: filterArchived.includes(opt.value) ? '#f8fafc' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterArchived.includes(opt.value) ? '#f8fafc' : 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={filterArchived.includes(opt.value)}
                          onChange={() => {
                            setFilterArchived(prev =>
                              prev.includes(opt.value) ? prev.filter(x => x !== opt.value) : [...prev, opt.value]
                            );
                          }}
                          style={{ accentColor: opt.color }}
                        />
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: opt.color
                          }} />
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                    <button
                      onClick={() => setFilterArchived(['false'])}
                      style={{
                        width: '100%',
                        padding: '6px',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#166534',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Réinitialiser (non archivées)
                    </button>
                  </div>
                </div>
              )}
            </div>

          </FilterBarResponsive>

          {/* Liste des interventions (paginée) */}
          <div style={{ marginBottom: '24px' }}>
            <ResponsiveCardList
              items={paginatedInterventions}
              renderCard={(intervention) => {
                const code = intervention.statut?.code || null;
                const mapped = code ? statutsMapping[code] : null;
                const isMapped = !!(mapped?.app_status && mapped.app_status !== 'ignored');
                const color = mapped?.color || '#6B7280';
                const dateStr = formatDateShort(intervention.date_intervention) || formatDateShort(intervention.dates?.emission);
                return (
                  <InterventionMobileCard
                    intervention={intervention}
                    statusColor={color}
                    statusIsMapped={isMapped}
                    formattedDate={dateStr || ''}
                    onOpenDetail={() => setDetailModal({ show: true, intervention })}
                    onOpenPassages={() => setPassagesModal({ show: true, intervention })}
                  />
                );
              }}
              fallback={renderInterventionsList(paginatedInterventions)}
              emptyMessage="Aucune intervention à afficher."
            />

            {/* Pagination */}
            {filteredInterventions.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: isPhone ? 'column' : 'row',
                justifyContent: isPhone ? 'flex-start' : 'space-between',
                alignItems: isPhone ? 'stretch' : 'center',
                gap: isPhone ? 10 : 0,
                padding: isPhone ? '12px' : '12px 20px',
                backgroundColor: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                borderRadius: '0 0 12px 12px',
                fontSize: '13px',
                color: '#64748b'
              }}>
                {/* Sélecteur nombre par page */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: isPhone ? 'center' : 'flex-start' }}>
                  <span>Afficher</span>
                  {[10, 20, 50].map(size => (
                    <button
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setCurrentPage(1);
                        localStorage.setItem('servup_or_pageSize', String(size));
                      }}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: pageSize === size ? '1px solid #3b82f6' : '1px solid #d1d5db',
                        backgroundColor: pageSize === size ? '#eff6ff' : 'white',
                        color: pageSize === size ? '#3b82f6' : '#374151',
                        fontWeight: pageSize === size ? 600 : 400,
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      {size}
                    </button>
                  ))}
                  <span>par page</span>
                </div>

                {/* Info page */}
                <span style={{ textAlign: isPhone ? 'center' : 'left' }}>
                  {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredInterventions.length)} sur {filteredInterventions.length}
                </span>

                {/* Navigation pages */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: isPhone ? 'center' : 'flex-start' }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage <= 1}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: safePage <= 1 ? '#d1d5db' : '#374151',
                      cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: safePage <= 1 ? '#d1d5db' : '#374151',
                      cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    ‹
                  </button>
                  <span style={{ padding: '0 8px', fontWeight: 600, color: '#1f2937' }}>
                    {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: safePage >= totalPages ? '#d1d5db' : '#374151',
                      cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage >= totalPages}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: safePage >= totalPages ? '#d1d5db' : '#374151',
                      cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Aucune donnée disponible
        </div>
      )}

      {/* Modal de détail intervention */}
      {detailModal.show && detailModal.intervention && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header modal — fixe en haut */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: 'white',
              flexShrink: 0
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1e293b',
                margin: 0
              }}>
                Détail intervention #{detailModal.intervention.numero_ordre || detailModal.intervention.external_id}
              </h2>
              <button
                onClick={() => setDetailModal({ show: false, intervention: null })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <CloseIcon size={24} color="#64748b" />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Section 1: Identification */}
              <div style={{
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0369a1',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <WrenchIcon size={16} color="#0369a1" />
                  Identification
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="N° Ordre" value={detailModal.intervention.numero_ordre || detailModal.intervention.external_id} />
                  <InfoField label="ID externe" value={detailModal.intervention.external_id} />
                  <InfoField label="ID Supabase" value={detailModal.intervention.id} mono />
                  <InfoField label="Statut" value={`${detailModal.intervention.statut?.code} - ${detailModal.intervention.statut?.libelle}`} />
                  <InfoField label="Statut applicatif" value={getAppStatusLabel(detailModal.intervention.statut?.app_status)} />
                  <InfoField label="Type réparation" value={detailModal.intervention.intervention?.type_reparation_code} />
                </div>
              </div>

              {/* Section 2: Dates */}
              <div style={{
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0369a1',
                  marginBottom: '12px'
                }}>
                  Dates
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="Date émission" value={formatDateShort(detailModal.intervention.dates?.emission)} />
                  <InfoField label="Date d'intervention prévue" value={formatDateShort(detailModal.intervention.dates?.prevue)} />
                  <InfoField label="Date intervention plannifiée" value={formatDateShort(detailModal.intervention.date_intervention)} />
                  <InfoField label="Date facture" value={formatDateShort(detailModal.intervention.dates?.facture)} />
                  <InfoField label="Date relance" value={formatDateShort(detailModal.intervention.dates?.relance)} />
                </div>
              </div>

              {/* Section 3: Client */}
              <div style={{
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #bbf7d0'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#166534',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <UserIcon size={16} color="#166534" />
                  Client
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Code client" value={detailModal.intervention.client?.code} />
                  <InfoField label="Nom" value={detailModal.intervention.client?.nom} />
                  <InfoField label="Nom 2" value={(detailModal.intervention.client as any)?.nom2} />
                  <InfoField label="Adresse" value={detailModal.intervention.client?.adresse1} />
                  <InfoField label="Adresse 2" value={detailModal.intervention.client?.adresse2} />
                  <InfoField label="Adresse 3" value={(detailModal.intervention.client as any)?.adresse3} />
                  <InfoField label="Complément" value={(detailModal.intervention.client as any)?.complement_ad1} />
                  <InfoField label="Complément 2" value={(detailModal.intervention.client as any)?.complement_ad2} />
                  <InfoField label="Code postal" value={detailModal.intervention.client?.code_postal} />
                  <InfoField label="Ville" value={detailModal.intervention.client?.ville} />
                  <InfoField label="Téléphone" value={detailModal.intervention.client?.telephone} />
                  <InfoField label="Tél. bureau" value={detailModal.intervention.client?.telephone_bureau} />
                  <InfoField label="Mobile" value={detailModal.intervention.client?.mobile} />
                  <InfoField label="Email" value={detailModal.intervention.client?.email} />
                  <InfoField label="Email bureau" value={(detailModal.intervention.client as any)?.email_bureau} />
                </div>
              </div>

              {/* Section 4: Appareil */}
              <div style={{
                backgroundColor: '#fefce8',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #fef08a'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#854d0e',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <WrenchIcon size={16} color="#854d0e" />
                  Appareil
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="Code article" value={detailModal.intervention.appareil?.code_article} />
                  <InfoField label="Désignation" value={(detailModal.intervention.appareil as any)?.designation} />
                  <InfoField label="Modèle" value={(detailModal.intervention.appareil as any)?.modele} />
                  <InfoField label="Marque" value={[detailModal.intervention.appareil?.marque_code, detailModal.intervention.appareil?.marque].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Type" value={[(detailModal.intervention.appareil as any)?.type_code, detailModal.intervention.appareil?.type].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Famille" value={[(detailModal.intervention.appareil as any)?.famille_code, (detailModal.intervention.appareil as any)?.famille].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Fournisseur" value={[(detailModal.intervention.appareil as any)?.fournisseur_code, (detailModal.intervention.appareil as any)?.fournisseur_nom].filter(Boolean).join(' - ') || null} />
                  <InfoField label="N° série" value={detailModal.intervention.appareil?.numero_serie} />
                  <InfoField label="Code EAN" value={detailModal.intervention.appareil?.code_ean} />
                  <InfoField label="Compteur" value={(detailModal.intervention.appareil as any)?.compteur != null ? String((detailModal.intervention.appareil as any).compteur) : null} />
                  <InfoField label="Date achat" value={formatDateShort(detailModal.intervention.appareil?.date_achat)} />
                  <InfoField label="État" value={[(detailModal.intervention.appareil as any)?.aspect_code, detailModal.intervention.appareil?.aspect].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Garantie" value={[(detailModal.intervention.appareil as any)?.garantie_code, detailModal.intervention.appareil?.garantie].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Réf. garantie" value={detailModal.intervention.appareil?.garantie_ref} />
                  <InfoField label="Nb accessoires" value={(detailModal.intervention.appareil as any)?.accessoires_nombre != null ? String((detailModal.intervention.appareil as any).accessoires_nombre) : null} />
                  <InfoField label="Accessoires" value={detailModal.intervention.appareil?.accessoires_libelle} />
                </div>
              </div>

              {/* Section 5: Panne & Compte-rendu */}
              <div style={{
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #fecaca'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#dc2626',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertTriangleIcon size={16} color="#dc2626" />
                  Panne & Compte-rendu
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  <InfoField label="Description panne" value={
                    (detailModal.intervention.panne as any)?.description
                    || detailModal.intervention.panne?.nature
                    || (detailModal.intervention.panne as any)?.code
                  } />
                  <InfoField label="Compte-rendu / Message facture" value={
                    detailModal.intervention.travaux?.compte_rendu
                    || detailModal.intervention.panne?.message_facture
                  } />
                  <InfoField label="Panne observée" value={(detailModal.intervention.panne as any)?.observee} />
                  <InfoField label="Travaux effectués" value={detailModal.intervention.travaux?.travaux_effectues} />
                  <InfoField label="Observations" value={detailModal.intervention.travaux?.observations} />
                </div>
              </div>

              {/* Section 6: Technicien */}
              <div style={{
                backgroundColor: '#faf5ff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9d5ff'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#7c3aed',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <UserIcon size={16} color="#7c3aed" />
                  Technicien
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Code" value={detailModal.intervention.technicien?.code} />
                  <InfoField label="Nom" value={detailModal.intervention.technicien?.nom} />
                  <InfoField label="Sous-traitant" value={detailModal.intervention.technicien?.sous_traitant_code} />
                  <InfoField label="Chez sous-traitant ?" value={detailModal.intervention.technicien?.chez_sous_traitant} />
                </div>
              </div>

              {/* Section 7: Logistique */}
              <div style={{
                backgroundColor: '#fff7ed',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #fed7aa'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#c2410c',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <TruckIcon size={16} color="#c2410c" />
                  Logistique
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="Type prestation" value={[detailModal.intervention.intervention?.type_prestation_code, detailModal.intervention.intervention?.type_prestation].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Type réparation" value={(detailModal.intervention.intervention as any)?.type_reparation_code} />
                  <InfoField label="Origine" value={[detailModal.intervention.intervention?.origine_code, detailModal.intervention.intervention?.origine].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Atelier" value={[detailModal.intervention.intervention?.atelier_code, detailModal.intervention.intervention?.atelier].filter(Boolean).join(' - ') || null} />
                  <InfoField label="Durée réparation" value={detailModal.intervention.intervention?.duree_reparation ? `${detailModal.intervention.intervention.duree_reparation} j` : null} />
                  <InfoField label="Emplacement" value={detailModal.intervention.intervention?.emplacement} />
                  <InfoField label="Opérateur" value={(detailModal.intervention as any).facturation?.utilisateur} />
                  <InfoField label="Alerte" value={(detailModal.intervention.intervention as any)?.alerte} />
                  <InfoField label="Immatriculation" value={(detailModal.intervention.intervention as any)?.immatriculation} />
                  <InfoField label="N° moteur" value={(detailModal.intervention.intervention as any)?.numero_moteur} />
                </div>
              </div>

              {/* Section 8: Montants */}
              <div style={{
                backgroundColor: '#ecfdf5',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #a7f3d0'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#065f46',
                  marginBottom: '12px'
                }}>
                  Montants
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="Montant max" value={detailModal.intervention.montants?.maxi != null ? `${detailModal.intervention.montants.maxi} €` : null} />
                  <InfoField label="Montant facturé" value={detailModal.intervention.montants?.facture != null ? `${detailModal.intervention.montants.facture} €` : null} />
                  <InfoField label="HT stock" value={detailModal.intervention.montants?.ht_stock != null ? `${detailModal.intervention.montants.ht_stock} €` : null} />
                  <InfoField label="Prix revient" value={detailModal.intervention.montants?.revient != null ? `${detailModal.intervention.montants.revient} €` : null} />
                  <InfoField label="Acompte 1" value={detailModal.intervention.montants?.acompte_1 != null ? `${detailModal.intervention.montants.acompte_1} €` : null} />
                  <InfoField label="Acompte 2" value={detailModal.intervention.montants?.acompte_2 != null ? `${detailModal.intervention.montants.acompte_2} €` : null} />
                  <InfoField label="Acompte 3" value={detailModal.intervention.montants?.acompte_3 != null ? `${detailModal.intervention.montants.acompte_3} €` : null} />
                  <InfoField label="Acompte 4" value={detailModal.intervention.montants?.acompte_4 != null ? `${detailModal.intervention.montants.acompte_4} €` : null} />
                  <InfoField label="Total pièces" value={(detailModal.intervention as any).totaux?.pieces != null ? `${(detailModal.intervention as any).totaux.pieces} €` : null} />
                  <InfoField label="Total main d'œuvre" value={(detailModal.intervention as any).totaux?.main_oeuvre != null ? `${(detailModal.intervention as any).totaux.main_oeuvre} €` : null} />
                  <InfoField label="Total TTC" value={(detailModal.intervention as any).totaux?.ttc != null ? `${(detailModal.intervention as any).totaux.ttc} €` : null} />
                </div>
              </div>

              {/* Section 9: Facturation & Quotas */}
              <div style={{
                backgroundColor: '#f5f3ff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #ddd6fe'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#5b21b6',
                  marginBottom: '12px'
                }}>
                  Facturation & Quotas
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="N° facture" value={(detailModal.intervention as any).facturation?.numero_facture != null ? String((detailModal.intervention as any).facturation.numero_facture) : null} />
                  <InfoField label="Code vendeur" value={(detailModal.intervention as any).facturation?.code_vendeur != null ? String((detailModal.intervention as any).facturation.code_vendeur) : null} />
                  <InfoField label="Opérateur" value={(detailModal.intervention as any).facturation?.utilisateur} />
                  <InfoField label="Date préfacturation" value={formatDateShort((detailModal.intervention as any).facturation?.date_prefacturation)} />
                  <InfoField label="Qté planifiée" value={(detailModal.intervention as any).quotas?.quantite_planifiee != null ? String((detailModal.intervention as any).quotas.quantite_planifiee) : null} />
                  <InfoField label="Qté réalisée" value={(detailModal.intervention as any).quotas?.quantite_realisee != null ? String((detailModal.intervention as any).quotas.quantite_realisee) : null} />
                  <InfoField label="Montant débit" value={(detailModal.intervention as any).quotas?.montant_debit != null ? `${(detailModal.intervention as any).quotas.montant_debit} €` : null} />
                  <InfoField label="Secteur" value={(detailModal.intervention as any).quotas?.secteur} />
                  <InfoField label="Code postal quota" value={(detailModal.intervention as any).quotas?.code_postal} />
                  <InfoField label="Date MAJ quota" value={formatDateShort((detailModal.intervention as any).quotas?.date_maj)} />
                  <InfoField label="Utilisateur quota" value={(detailModal.intervention as any).quotas?.utilisateur} />
                  <InfoField label="Date débit" value={formatDateShort((detailModal.intervention as any).quotas?.date_debit)} />
                </div>
              </div>

              {/* Section 10: Signature */}
              <div style={{
                backgroundColor: '#fffbeb',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #fde68a'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#92400e',
                  marginBottom: '12px'
                }}>
                  Signature client
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Signature" value={(detailModal.intervention as any).signature?.data ? 'Oui' : null} />
                  <InfoField label="Date signature" value={formatDateShort((detailModal.intervention as any).signature?.date)} />
                </div>
              </div>

              {/* Section 11: Archivage */}
              <div style={{
                backgroundColor: '#fdf2f8',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #fbcfe8'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#be185d',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <ArchiveIcon size={16} color="#be185d" />
                  Archivage
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Archivé ?" value={detailModal.intervention.archivage?.is_archived ? 'Oui' : 'Non'} />
                  <InfoField label="Raison" value={detailModal.intervention.archivage?.archived_reason} />
                  <InfoField label="Date archivage" value={detailModal.intervention.archivage?.archived_at ? formatDate(detailModal.intervention.archivage.archived_at) : null} />
                  <InfoField label="Archivé par" value={(detailModal.intervention.archivage as any)?.archived_by} />
                  <InfoField label="Origine archivage" value={(detailModal.intervention.archivage as any)?.archived_origin} />
                </div>
              </div>

              {/* Section 12: Métadonnées */}
              <div style={{
                backgroundColor: '#f1f5f9',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #cbd5e1'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748b',
                  marginBottom: '12px'
                }}>
                  Métadonnées
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <InfoField label="Créé le" value={(detailModal.intervention.metadata as any)?.created_at ? formatDate((detailModal.intervention.metadata as any).created_at) : null} />
                  <InfoField label="Modifié le" value={(detailModal.intervention.metadata as any)?.updated_at ? formatDate((detailModal.intervention.metadata as any).updated_at) : null} />
                  <InfoField label="Créé par" value={(detailModal.intervention.metadata as any)?.created_by} />
                  <InfoField label="Date création" value={formatDateShort((detailModal.intervention.metadata as any)?.created_date)} />
                  <InfoField label="Date clôture" value={formatDateShort((detailModal.intervention.metadata as any)?.closed_date)} />
                  <InfoField label="Source externe" value={(detailModal.intervention.metadata as any)?.external_source} />
                  <InfoField label="ID externe" value={(detailModal.intervention.metadata as any)?.external_id} />
                  <InfoField label="Sync externe le" value={(detailModal.intervention.metadata as any)?.external_synced_at ? formatDate((detailModal.intervention.metadata as any).external_synced_at) : null} />
                  <InfoField label="Statut sync" value={(detailModal.intervention.metadata as any)?.sync_status} />
                  <InfoField label="Direction sync" value={(detailModal.intervention.metadata as any)?.sync_direction} />
                  <InfoField label="Dernier sync" value={(detailModal.intervention.metadata as any)?.last_synced_at ? formatDate((detailModal.intervention.metadata as any).last_synced_at) : null} />
                  <InfoField label="Erreur sync" value={(detailModal.intervention.metadata as any)?.sync_error} />
                  <InfoField label="Tentatives sync" value={(detailModal.intervention.metadata as any)?.sync_attempts != null ? String((detailModal.intervention.metadata as any).sync_attempts) : null} />
                  <InfoField label="Version sync" value={(detailModal.intervention.metadata as any)?.sync_version != null ? String((detailModal.intervention.metadata as any).sync_version) : null} />
                  <InfoField label="Source payload" value={(detailModal.intervention.metadata as any)?.payload_source} />
                </div>
              </div>

            </div>
            </div>

            {/* Footer modal — fixe en bas */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              flexShrink: 0
            }}>
              <button
                onClick={() => setDetailModal({ show: false, intervention: null })}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal historique des passages */}
      {passagesModal.show && passagesModal.intervention && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px',
            width: '900px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
            padding: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0'
            }}>
              <div>
                <h2 style={{
                  fontSize: '20px', fontWeight: 700, color: '#1e293b',
                  display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px'
                }}>
                  <HistoryIcon size={24} color="#8b5cf6" />
                  Historique des interventions
                </h2>
                <p style={{ color: '#64748b', fontSize: '14px' }}>
                  OR n° {passagesModal.intervention.numero_ordre || passagesModal.intervention.external_id} - {passagesModal.intervention.client?.nom || 'Client'}
                </p>
              </div>
              <button
                onClick={() => setPassagesModal({ show: false, intervention: null })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <CloseIcon size={24} color="#64748b" />
              </button>
            </div>

            {/* Contenu */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Loading */}
              {passagesLoading && (
                <div style={{ backgroundColor: '#f5f3ff', borderRadius: '8px', padding: '40px', border: '1px solid #ddd6fe', textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #ddd6fe', borderTopColor: '#8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: '16px', color: '#7c3aed', fontSize: '14px' }}>Chargement des interventions...</div>
                </div>
              )}

              {/* Aucun passage */}
              {!passagesLoading && (!passagesData?.passages || passagesData.passages.length === 0) && (
                <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '40px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                  <div style={{ color: '#64748b', fontSize: '15px', fontWeight: 500 }}>Aucune intervention enregistrée pour cet OR</div>
                  <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>Les interventions apparaîtront ici lorsqu'un technicien commencera le travail</div>
                </div>
              )}

              {/* Liste des passages */}
              {!passagesLoading && passagesData?.passages && passagesData.passages.length > 0 && (
                <div style={{ backgroundColor: '#f5f3ff', borderRadius: '8px', padding: '16px', border: '1px solid #ddd6fe' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#7c3aed', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WrenchIcon size={18} color="#7c3aed" />
                    {passagesData.passages.length} intervention{passagesData.passages.length > 1 ? 's' : ''}
                  </h3>

                  {passagesData.passages.map((passage, idx) => {
                    const isFinal = passage.statut_final === '90' || passage.statut_final === '99';
                    return (
                    <div key={passage.passage_number} style={{ backgroundColor: 'white', borderRadius: '12px', marginBottom: idx < passagesData.passages.length - 1 ? '16px' : 0, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                      {/* En-tête passage */}
                      <div style={{
                        background: isFinal
                          ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                          : 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                        padding: '12px 16px', color: 'white'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', fontWeight: 700, fontSize: '14px' }}>
                              {passage.passage_number}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '15px' }}>
                                Intervention #{passage.passage_number}
                                {isFinal && <span style={{ marginLeft: '8px' }}>✓</span>}
                              </div>
                              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                                {passage.started_at ? new Date(passage.started_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Date inconnue'}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {passage.statut_initial && passage.statut_final && (
                              <span style={{ padding: '4px 10px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                                {getStatutLibelle(passage.statut_initial)} → {getStatutLibelle(passage.statut_final)}
                              </span>
                            )}
                            {passage.duration_minutes != null && passage.duration_minutes > 0 && (
                              <span style={{ padding: '4px 10px', backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '6px', fontSize: '13px', fontWeight: 600 }}>
                                ⏱️ {Math.floor(passage.duration_minutes / 60)}h{(passage.duration_minutes % 60).toString().padStart(2, '0')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Contenu du passage */}
                      <div style={{ padding: '16px' }}>
                        {/* Résumé rapide */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ backgroundColor: '#f0f9ff', padding: '10px', borderRadius: '8px', textAlign: 'center', border: '1px solid #bae6fd' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#0284c7' }}>{passage.photos.length}</div>
                            <div style={{ fontSize: '11px', color: '#0369a1' }}>Photos</div>
                          </div>
                          <div style={{ backgroundColor: '#fffbeb', padding: '10px', borderRadius: '8px', textAlign: 'center', border: '1px solid #fef3c7' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#d97706' }}>{passage.pieces.length}</div>
                            <div style={{ fontSize: '11px', color: '#92400e' }}>Pièces</div>
                          </div>
                          <div style={{ backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '8px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#16a34a' }}>
                              {passage.payments.length > 0 ? passage.payments.reduce((sum: number, p: PassagePayment) => sum + (p.amount || 0), 0).toFixed(0) + ' €' : '0 €'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#166534' }}>Encaissé</div>
                          </div>
                          <div style={{ backgroundColor: '#faf5ff', padding: '10px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e9d5ff' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#9333ea' }}>{passage.lines.filter(l => l.observations).length}</div>
                            <div style={{ fontSize: '11px', color: '#7c3aed' }}>Notes</div>
                          </div>
                        </div>

                        {/* Compte-rendu */}
                        {passage.lines.filter(l => l.observations).length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', marginBottom: '8px' }}>📝 Compte-rendu</div>
                            <div style={{ backgroundColor: '#faf5ff', borderRadius: '8px', padding: '12px', border: '1px solid #e9d5ff', fontSize: '13px', color: '#1e293b', lineHeight: 1.6 }}>
                              {passage.lines.filter(l => l.observations).map(l => l.observations).join(' • ')}
                            </div>
                          </div>
                        )}

                        {/* Pièces utilisées */}
                        {passage.pieces.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', textTransform: 'uppercase', marginBottom: '8px' }}>🔧 Pièces utilisées</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {passage.pieces.map((piece) => {
                                // Pièce manuelle = pas de code catalogue (article_code). On retombe alors
                                // sur la référence libre (article_reference) saisie par le technicien.
                                const isManual = !piece.article_code;
                                const ref = piece.article_code || piece.article_reference || '';
                                const label = piece.article_description || '';
                                const text = ref && label ? `${ref} - ${label}` : (ref || label || 'Pièce');
                                return (
                                  <span
                                    key={piece.id}
                                    title={isManual ? 'Pièce ajoutée manuellement' : undefined}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '4px 10px',
                                      backgroundColor: '#fffbeb',
                                      color: '#92400e',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      border: isManual ? '1px dashed #d97706' : '1px solid #fef3c7'
                                    }}
                                  >
                                    {isManual && <span aria-label="Pièce ajoutée manuellement">✏️</span>}
                                    {text} × {piece.quantity}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Galerie photos */}
                        {passage.photos.length > 0 && (
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#0369a1', textTransform: 'uppercase', marginBottom: '8px' }}>📷 Photos ({passage.photos.length})</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                              {passage.photos.map((photo) => (
                                <div key={photo.id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #bae6fd', backgroundColor: '#f0f9ff', cursor: 'pointer' }}
                                  onClick={() => photo.url && window.open(photo.url, '_blank')}
                                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                  {photo.url ? (
                                    <img src={photo.url} alt={photo.description || 'Photo'} style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                                  ) : (
                                    <div style={{ width: '100%', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '11px' }}>Photo non disponible</div>
                                  )}
                                  {photo.description && (
                                    <div style={{ padding: '6px 8px', backgroundColor: 'white', fontSize: '11px', color: '#475569', borderTop: '1px solid #e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={photo.description}>
                                      {photo.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}

                  {/* Récapitulatif global */}
                  {passagesData.totaux && (
                    <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '2px solid #8b5cf6' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px', textTransform: 'uppercase' }}>Récapitulatif intervention</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#7c3aed' }}>{passagesData.totaux.passages_count}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Interventions</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#92400e' }}>{passagesData.totaux.pieces_count}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Pièces</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#7c3aed' }}>
                            {Math.floor((passagesData.totaux.duree_totale_minutes || 0) / 60)}h{((passagesData.totaux.duree_totale_minutes || 0) % 60).toString().padStart(2, '0')}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Durée totale</div>
                        </div>
                      </div>
                      {/* Détail encaissements */}
                      <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#166534', textTransform: 'uppercase', marginBottom: '8px' }}>💰 Encaissements</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', textAlign: 'center' }}>
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#166534' }}>{(passagesData.totaux.payments_total || 0).toFixed(0)} €</div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>Total</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1d4ed8' }}>{(passagesData.totaux.payments_card || 0).toFixed(0)} €</div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>CB</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>{(passagesData.totaux.payments_cash || 0).toFixed(0)} €</div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>Espèces</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#7c3aed' }}>{(passagesData.totaux.payments_check || 0).toFixed(0)} €</div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>Chèque</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(passagesData?.bons_intervention?.length || 0) > 0 ? (
                  passagesData?.bons_intervention?.map((bon, idx) => (
                    <button
                      key={idx}
                      onClick={() => window.open(bon.url, '_blank')}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: idx === 0 ? '#16a34a' : '#f0fdf4',
                        color: idx === 0 ? 'white' : '#166534',
                        border: idx === 0 ? 'none' : '1px solid #bbf7d0',
                        borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: '6px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = idx === 0 ? '#15803d' : '#dcfce7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx === 0 ? '#16a34a' : '#f0fdf4'}
                    >
                      📄 Bon d'intervention n°{bon.passage_number}
                    </button>
                  ))
                ) : (
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>📄 Aucun bon d'intervention</span>
                )}
              </div>
              <button
                onClick={() => setPassagesModal({ show: false, intervention: null })}
                style={{ padding: '10px 24px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation d'archivage */}
      {archiveModal.show && archiveModal.intervention && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#fdf2f8',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ArchiveIcon size={20} color="#ec4899" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#be185d' }}>
                  Archiver l'intervention
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                  L'intervention sera déplacée vers les archives
                </p>
              </div>
            </div>

            <div style={{
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#475569' }}>
                Voulez-vous archiver cette intervention ?
              </p>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                <strong>N° Ordre :</strong> {archiveModal.intervention.numero_ordre || archiveModal.intervention.external_id}<br />
                <strong>Client :</strong> {archiveModal.intervention.client?.nom || '-'}<br />
                <strong>Statut :</strong> {archiveModal.intervention.statut?.code} - {archiveModal.intervention.statut?.libelle}
              </div>
            </div>

            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fdf2f8',
              border: '1px solid #fbcfe8',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '13px', color: '#be185d' }}>
                <strong>Note :</strong> L'intervention sera conservée dans les archives et ne sera plus visible dans la liste principale.
                Elle restera accessible via le filtre "Archivées".
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setArchiveModal({ show: false, intervention: null })}
                disabled={archiving}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => archiveIntervention(archiveModal.intervention!.id)}
                disabled={archiving}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ec4899',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: archiving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: archiving ? 0.7 : 1
                }}
              >
                {archiving ? (
                  <>
                    <RefreshIcon size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Archivage...
                  </>
                ) : (
                  <>
                    <ArchiveIcon size={16} />
                    Archiver
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox pour afficher les photos en grand */}
      {lightboxPhoto && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
          >
            <CloseIcon size={24} color="white" />
          </button>

          <div
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxPhoto.url}
              alt={lightboxPhoto.description || 'Photo intervention'}
              style={{
                maxWidth: '100%',
                maxHeight: lightboxPhoto.description ? 'calc(90vh - 80px)' : '90vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            {lightboxPhoto.description && (
              <div style={{
                marginTop: '16px',
                padding: '12px 20px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                maxWidth: '600px',
                textAlign: 'center'
              }}>
                <div style={{ color: 'white', fontSize: '14px', lineHeight: 1.5 }}>
                  {lightboxPhoto.description}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Composant InfoField pour afficher un champ avec label
const InfoField = ({ label, value, mono, badge, error }: {
  label: string;
  value: string | undefined | null;
  mono?: boolean;
  badge?: 'success' | 'warning' | 'error';
  error?: boolean;
}) => (
  <div>
    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{
      fontSize: '13px',
      color: error ? '#dc2626' : '#1e293b',
      fontFamily: mono ? 'monospace' : 'inherit',
      wordBreak: 'break-word'
    }}>
      {badge ? (
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '4px',
          backgroundColor: badge === 'success' ? '#dcfce7' : badge === 'warning' ? '#fef3c7' : '#fee2e2',
          color: badge === 'success' ? '#166534' : badge === 'warning' ? '#92400e' : '#dc2626',
          fontWeight: 500
        }}>
          {value || '-'}
        </span>
      ) : (
        value || '-'
      )}
    </div>
  </div>
);

export default InterventionsPage;
